import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { LuTrash2 } from 'react-icons/lu'
import { CustomerAvatar } from '@/frontend/components/marina/CustomerAvatar'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { useCustomer, useDeleteCustomer } from '@/frontend/hooks/useCustomers'

export const Route = createFileRoute('/dashboard/accounts/customers/$id')({
  component: CustomerDetail,
})

function CustomerDetail() {
  const { id } = Route.useParams()
  const { data, isLoading } = useCustomer(id)
  const del = useDeleteCustomer()
  const navigate = useNavigate()

  if (isLoading)
    return (
      <Container size="xl" py="md">
        Loading…
      </Container>
    )
  if (!data)
    return (
      <Container size="xl" py="md">
        Not found
      </Container>
    )
  const c = data.customer
  const fullName = `${c.firstName} ${c.lastName}`.trim()
  const counts = c._count ?? { boats: 0, bookings: 0, contracts: 0, invoices: 0, quotes: 0 }
  const blockers = [
    counts.boats && `${counts.boats} boat${counts.boats > 1 ? 's' : ''}`,
    counts.bookings && `${counts.bookings} booking${counts.bookings > 1 ? 's' : ''}`,
    counts.contracts && `${counts.contracts} contract${counts.contracts > 1 ? 's' : ''}`,
    counts.invoices && `${counts.invoices} invoice${counts.invoices > 1 ? 's' : ''}`,
  ].filter(Boolean) as string[]
  const canDelete = blockers.length === 0

  function handleDelete() {
    modals.openConfirmModal({
      title: 'Delete customer?',
      children: (
        <Text size="sm">
          Anda yakin ingin menghapus <b>{fullName}</b>? Aksi ini tidak bisa dibatalkan.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        del.mutate(id, {
          onSuccess: () => {
            notifications.show({ color: 'teal', message: 'Customer deleted' })
            navigate({ to: '/dashboard/accounts/customers' })
          },
          onError: (e: any) => {
            notifications.show({
              color: 'red',
              title: 'Delete failed',
              message: e?.message || 'Gagal menghapus customer',
            })
          },
        })
      },
    })
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/accounts/customers" c="dimmed">
            Customers
          </Anchor>
          <span>{fullName}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md">
          <Group wrap="nowrap" gap="lg" align="flex-start">
            <CustomerAvatar name={fullName} email={c.email} size={72} />
            <div style={{ flex: 1 }}>
              <Group gap="xs" mb={4}>
                <Title order={3}>{fullName}</Title>
                <KindBadge category={c.category} />
                <Badge variant="light" color="gray">
                  {c.type}
                </Badge>
              </Group>
              <Text c="dimmed" size="sm">
                {c.email || '—'} · {c.phone || '—'} · {c.country || '—'}
              </Text>
            </div>
            <Tooltip label={canDelete ? '' : `Hapus dulu: ${blockers.join(', ')}`} disabled={canDelete} withArrow>
              <Button
                color="red"
                variant="light"
                leftSection={<LuTrash2 size={16} />}
                disabled={!canDelete || del.isPending}
                loading={del.isPending}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Tooltip>
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Counts
              </Text>
              <Group gap="md">
                <Stat label="Boats" value={c._count?.boats ?? 0} />
                <Stat label="Bookings" value={c._count?.bookings ?? 0} />
                <Stat label="Invoices" value={c._count?.invoices ?? 0} />
                <Stat label="Contracts" value={c._count?.contracts ?? 0} />
                <Stat label="Quotes" value={c._count?.quotes ?? 0} />
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Boats
              </Text>
              {c.boats && c.boats.length > 0 ? (
                <Stack gap="xs">
                  {c.boats.map((b) => (
                    <Group key={b.id} justify="space-between">
                      <Text size="sm">{b.name}</Text>
                      <Text component="a" href={`/dashboard/accounts/boats/${b.id}`} size="sm" c="laut">
                        View →
                      </Text>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" size="sm">
                  No boats yet.
                </Text>
              )}
            </Card>
          </Grid.Col>

          <Grid.Col span={12}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Contact
              </Text>
              <Grid>
                <Info label="Address" value={c.address} />
                <Info label="City" value={c.city} />
                <Info label="Country" value={c.country} />
                <Info label="Postal Code" value={c.postalCode} />
                <Info label="Mobile" value={c.mobile} />
                <Info label="Website" value={c.website} />
              </Grid>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 80 }}>
      <Text fw={700} size="xl" c="laut.7">
        {value}
      </Text>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </Grid.Col>
  )
}
